<template>
  <div id="summary-page">
    <v-container>
      <v-alert type="error" v-if="!finalTotal">
        Whoops! It looks like we have no data in our calculator. Let's fill in
        some values!
      </v-alert>

      <div class="display-1 my-3">Summary</div>
      <v-divider class="mt-1"></v-divider>

      <v-layout row wrap>
        <v-flex class="pa-2" xs12 md6 lg5>
          <v-card class="pb-5" height="12rem" outlined>
            <ChartCardTitle
              color="blue"
              title="Cost of Poor Quality"
              :redirectEnabled="false"
            />
            <v-card-title class="display-3">
              {{ formattedFinalTotal }}
              <v-spacer></v-spacer>
              <v-btn outlined color="green lighten-1" @click="printSummary">
                <v-icon small>fas fa-print</v-icon>
                <div class="has-margin-left-1">Print Report</div>
              </v-btn>
            </v-card-title>
          </v-card>
          <v-card class="mt-3" outlined>
            <PieChart
              :costSectionTotals="costSectionTotals"
              :finalTotal="finalTotal"
            />
          </v-card>
        </v-flex>
        <v-flex class="pa-2" xs12 md6 lg7>
          <v-card class="pb-4" outlined>
            <ChartCardTitle
              color="purple"
              title="Total Costs vs. Optimal Conditions"
              :redirectEnabled="false"
            />
            <ColumnChart
              :costSectionTotals="costSectionTotals"
              :optimalConditions="optimalConditions"
              :finalTotal="finalTotal"
            />
          </v-card>
        </v-flex>
      </v-layout>

      <div class="display-1 my-3">Opportunity Breakdown</div>
      <v-divider class="mt-1"></v-divider>

      <v-layout row wrap>
        <v-flex class="pa-2" xs12 md6 lg6>
          <v-card class="pb-5" height="12rem" outlined>
            <ChartCardTitle
              color="blue"
              title="We have calculated a way for you to save:"
              :redirectEnabled="false"
            />
            <v-card-title class="display-3">
              ${{ formattedCOPQ }}
              <v-spacer></v-spacer>
              <v-btn
                outlined
                color="blue lighten-1"
                @click="$vuetify.goTo(2390)"
              >
                <v-icon small>fas fa-exclamation-circle</v-icon>
                <div class="has-margin-left-1">Find out more</div>
              </v-btn>
            </v-card-title>
          </v-card>
        </v-flex>
      </v-layout>

      <div class="display-1 my-3">Cost Section Analysis</div>
      <v-divider class="mt-1"></v-divider>

      <section>
        <v-layout row wrap>
          <v-flex
            v-for="(costSection, index) in costSections"
            :key="index"
            xs12
            md6
          >
            <v-card class="has-margin-1" v-if="index !== 4" outlined>
              <ChartCardTitle
                :color="costSection.color"
                :title="costSection.title"
                :index="index"
              />
              <ParetoChart :costSection="costSection" />
            </v-card>
          </v-flex>
        </v-layout>
        <v-layout row>
          <v-flex>
            <v-card class="has-margin-1" outlined>
              <ChartCardTitle
                :color="costSections[4].color"
                :title="costSections[4].title"
                :index="4"
              />
              <ParetoChart :costSection="costSections[4]" />
            </v-card>
          </v-flex>
        </v-layout>
      </section>
    </v-container>

    <v-parallax height="380" :src="require(`@/assets/images/seattle-1.jpg`)">
      <v-layout column align-center justify-center>
        <img
          src="@/assets/images/LeanRevisions-hLogo.png"
          alt="lean revisions logo"
          width="350"
        />
        <div class="headline white--text mb-4 text-center">
          Yes! We offer free consultations.
        </div>
        <em
          >Our mission is to help companies like yours minimize their Costs of
          Poor Quality.</em
        >
        <em class="mb-6"
          >Fill out the form with any requests or questions and we'll be in
          touch.</em
        >
      </v-layout>
    </v-parallax>

    <section>
      <v-container>
        <section class="my-12">
          <ContactForm />
        </section>
      </v-container>
    </section>
  </div>
</template>

<script>
import ColumnChart from "@/components/SummaryDashboard/ColumnChart";
import ParetoChart from "@/components/SummaryDashboard/ParetoChart";
import PieChart from "@/components/SummaryDashboard/PieChart";
import ContactForm from "@/components/Forms/ContactForm";
import ChartCardTitle from "@/components/SummaryDashboard/ChartCardTitle";

import GeneralService from "@/services/GeneralService";

export default {
  name: "summary-page",
  components: {
    ColumnChart,
    ParetoChart,
    PieChart,
    ContactForm,
    ChartCardTitle,
  },
  props: {
    thePage: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      costSectionDetails: [
        {
          parallaxColor: "orange",
          primaryColor: "#008FFB",
          title: "Prevention",
        },
        { parallaxColor: "red", primaryColor: "#00E396", title: "Appraisal" },
        {
          parallaxColor: "purple",
          primaryColor: "#FEB019",
          title: "Internal Failure",
        },
        {
          parallaxColor: "blue",
          primaryColor: "#E14560",
          title: "External Failure",
        },
        {
          parallaxColor: "light-blue",
          primaryColor: "#775DD0",
          title: "Other 8 Types of Waste",
        },
      ],
    };
  },
  watch: {
    thePage: {
      immediate: true,
      handler(val) {
        this.$emit("update:page", val);
      },
    },
  },
  computed: {
    costSections() {
      const costSections = this.$store.state.costSections;

      return costSections.map((costSection, index) => {
        const sortedCosts = this.sortByDecreasingTotals(
          this.formatCosts(costSection.costs)
        );
        const sortedCostTotals = this.reduceCostTotals(sortedCosts);
        const sortedNames = sortedCosts.map((cost) => cost.name);
        const sortedParetoPoints = this.paretoPoints(sortedCostTotals);

        const costSectionDetails = this.costSectionDetails[index];
        const color = costSectionDetails.parallaxColor;
        const primaryColors = [costSectionDetails.primaryColor, "#000000"];
        const title = costSectionDetails.title;

        return {
          ...costSection,
          color,
          title,
          primaryColors,
          sortedCostTotals,
          sortedNames,
          sortedParetoPoints,
        };
      });
    },
    costSectionTotals() {
      return this.costSections.map((costSection) => {
        const costTotals = this.reduceCostTotals(costSection.costs);
        return costTotals.reduce(GeneralService.reducer);
      });
    },
    optimalConditions() {
      return this.costSectionTotals.map(
        (costSectionTotal) => costSectionTotal * 0.6
      );
    },
    finalTotal() {
      return this.costSectionTotals.reduce(GeneralService.reducer);
    },
    formattedFinalTotal() {
      return `$${GeneralService.dollarFormatted(this.finalTotal)}`;
    },
    COPQ() {
      const totalOptimalConditions = this.optimalConditions
        .reduce((a, b) => a + b)
        .toFixed(0);
      return this.finalTotal - totalOptimalConditions;
    },
    formattedCOPQ() {
      return GeneralService.dollarFormatted(this.COPQ);
    },
    COPQbreakdown() {
      return this.costSectionTotals.map((cost) =>
        parseInt((cost - cost * 0.6).toFixed(0))
      );
    },
  },
  methods: {
    formatCosts(costs) {
      return costs.map((cost) => {
        const values = cost.subCosts.map((subCost) => {
          return subCost.value ? Number(subCost.value) : 0;
        });
        const costTotal = values.reduce(GeneralService.reducer);
        return { ...cost, costTotal };
      });
    },
    paretoPoints(costTotals) {
      const total = costTotals.reduce(GeneralService.reducer);
      var accrument = 0;
      return costTotals.map((costTotal) => {
        const percentage = (costTotal / total) * 100;
        const point = percentage + accrument;
        accrument += percentage;
        return Math.ceil(point);
      });
    },
    printSummary() {
      window.print();
    },
    redirectToCosts(activeStep) {
      this.$router.push({
        name: "calculator",
        params: {
          thePage: 2,
        },
      });
    },
    reduceCostTotals(costs) {
      return costs.map((cost) => {
        const values = cost.subCosts.map((subCost) => {
          return subCost.value ? Number(subCost.value) : 0;
        });
        return values.reduce(GeneralService.reducer);
      });
    },
    sortByDecreasingTotals(costSectionCosts) {
      return costSectionCosts.sort((a, b) =>
        a.costTotal > b.costTotal ? -1 : 1
      );
    },
  },
  created: function() {
    GeneralService.scrollTop();
  },
};
</script>

<style lang="scss" scoped>
#opportunity {
  color: $copq-green;
  font-size: 5rem;
}
</style>
